import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import './AlterarSenha.css'

const AlterarSenha = () => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [usuario, setUsuario] = useState(null)
  const [mensagem, setMensagem] = useState('')
  
  const navigate = useNavigate()
  const location = useLocation()
  const currentPasswordRef = useRef(null)

  useEffect(() => {
    // Verificar se o usuário está autenticado
    const usuarioStorage = localStorage.getItem('usuario')
    if (!usuarioStorage) {
      navigate('/login')
      return
    }

    const usuarioData = JSON.parse(usuarioStorage)
    setUsuario(usuarioData)

    // Verificar se há mensagem específica (ex: primeiro acesso)
    if (location.state?.mensagem) {
      setMensagem(location.state.mensagem)
    }

    // Focar no campo de senha atual quando o modal abrir
    const timer = setTimeout(() => {
      if (currentPasswordRef.current) {
        currentPasswordRef.current.focus()
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [navigate, location])

  // Função para lidar com teclas (ESC para fechar)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        navigate('/welcome')
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  // Função para tratamento consistente de erros
  const handleError = (error, userMessage) => {
    console.error('Erro técnico:', error)
    setError(userMessage || 'Ocorreu um erro inesperado. Tente novamente.')
  }

  // Validação do formulário
  const validateForm = () => {
    if (!currentPassword.trim()) {
      setError('Senha atual é obrigatória')
      return false
    }
    
    if (!newPassword.trim()) {
      setError('Nova senha é obrigatória')
      return false
    }
    
    if (newPassword.length < 6) {
      setError('Nova senha deve ter pelo menos 6 caracteres')
      return false
    }
    
    if (newPassword !== confirmPassword) {
      setError('Nova senha e confirmação não coincidem')
      return false
    }
    
    if (newPassword === currentPassword) {
      setError('Nova senha deve ser diferente da senha atual')
      return false
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Usar a função personalizada do banco para alterar senha
      const { data, error } = await supabase
        .rpc('alterar_senha_usuario', {
          p_usuario_id: usuario.id,
          p_senha_atual: currentPassword,
          p_nova_senha: newPassword
        })

      if (error) throw error

      if (data && data.length > 0) {
        const resultado = data[0]
        
        if (resultado.sucesso) {
          setSuccess(resultado.mensagem)
          
          // Limpar formulário
          setCurrentPassword('')
          setNewPassword('')
          setConfirmPassword('')
          
          // Aguardar um pouco e redirecionar
          setTimeout(() => {
            navigate('/welcome')
          }, 2000)
        } else {
          setError(resultado.mensagem)
        }
      } else {
        setError('Resposta inválida do servidor')
      }
    } catch (error) {
      handleError(error, 'Erro ao alterar senha. Verifique os dados informados.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/welcome')
  }

  // Função para calcular força da senha
  const getPasswordStrength = (password) => {
    if (password.length < 4) return { class: 'weak', text: 'Senha fraca' }
    if (password.length < 6) return { class: 'medium', text: 'Senha média' }
    return { class: 'strong', text: 'Senha forte' }
  }

  if (!usuario) {
    return <div>Carregando...</div>
  }

  return (
    <div className="password-change-modal">
      <div className="password-change-container">
        <div className="password-change-header">
          <div className="security-icon">
            <div className="icon-wrapper">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <h1>Configuração de Segurança</h1>
          <p className="welcome-text">Bem-vindo, <span className="user-name">{usuario.nome}</span></p>
          <p className="instruction-text">Para sua segurança, defina uma nova senha para acessar o sistema.</p>
          {mensagem && (
            <div className="info-banner">
              <div className="info-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="info-content">{mensagem}</div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-section">
            <div className="input-group">
              <label htmlFor="currentPassword" className="input-label">
                <span className="label-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </span>
                Senha Atual
              </label>
              <input
                ref={currentPasswordRef}
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite sua senha atual"
                required
                disabled={loading}
                autoFocus
                className="password-input"
              />
            </div>

            <div className="input-group">
              <label htmlFor="newPassword" className="input-label">
                <span className="label-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Nova Senha
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite sua nova senha (mín. 6 caracteres)"
                required
                disabled={loading}
                minLength={6}
                className="password-input"
              />
              {newPassword && (
                <div className="password-strength-indicator">
                  <div className="strength-bar">
                    <div className={`strength-fill ${getPasswordStrength(newPassword).class}`}></div>
                  </div>
                  <span className="strength-label">{getPasswordStrength(newPassword).text}</span>
                </div>
              )}
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword" className="input-label">
                <span className="label-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </span>
                Confirmar Nova Senha
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                required
                disabled={loading}
                className="password-input"
              />
              {confirmPassword && (
                <div className={`password-match-indicator ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                  <span className="match-icon">
                    {newPassword === confirmPassword ? '✓' : '✗'}
                  </span>
                  <span className="match-text">
                    {newPassword === confirmPassword ? 'Senhas coincidem' : 'Senhas não coincidem'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="alert-message error">
              <div className="alert-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                  <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="alert-content">
                <strong>Erro:</strong> {error}
              </div>
            </div>
          )}

          {success && (
            <div className="alert-message success">
              <div className="alert-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="alert-content">
                <strong>Sucesso:</strong> {success}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              <span className="btn-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <span className="btn-icon">
                {loading ? (
                  <svg className="spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AlterarSenha